'use client'

import React, { ReactElement, useEffect, useState } from 'react'
import { DropDownFooterProps } from '@/components/ui/wizard/DropDownFooter'
import DropDown from '@/components/ui/wizard/DropDown'
import { useDispatch } from 'react-redux'
import { SearchItem } from '@/types/Page'
import { SearchResult } from '@/components/ui/wizard/Wizard'
import { AnyAction } from 'redux'
import classNames from 'classnames'
import { WIZARD_INPUT_KEY_PRESSED } from '@/lib/sharedConstants'
import { useTranslations } from 'next-intl'

interface SearchBoxProps extends DropDownFooterProps {
  resultSearchTerm: string
  searchResults: SearchResult[] | undefined
  selectedItems: SearchItem[]
  setSelectedItems: (items: SearchItem[]) => AnyAction
  searchTerm: string
  searchWizard: boolean | undefined
}

interface SingleSearchResultProps extends SearchItem {
  selectedItems: SearchItem[]
  setSelectedItems: (items: SearchItem[]) => AnyAction
  highlighted: boolean
  searchWizard: boolean | undefined
  highlightWithIndex: boolean
}

function SingleSearchResult(props: SingleSearchResultProps): ReactElement {
  const dispatch = useDispatch()

  function addItem(): void {
    dispatch(
      props.setSelectedItems([
        ...props.selectedItems,
        { type: props.type, value: props.value, label: props.label, text: props.text },
      ]),
    )
  }

  return (
    <div
      className={classNames({
        'break-word w-[100%] max-w-[100%] cursor-pointer whitespace-normal text-stone-600 text-stone-700 wd:px-2 wd:pb-1.5 wd:pt-1.5 wd:text-md wd:hover:bg-[#F3F3F3] wd:hover:text-primary wd:hover:underline wm:px-1 wm:pb-2.5 wm:pl-[14px] wm:pr-[14px] wm:pt-2.5 wm:text-lg wm:font-medium wm:active:bg-primary-lightened wm:active:text-primary':
          props.searchWizard,
        'break-word w-[100%] max-w-[100%] cursor-pointer whitespace-normal text-stone-600 text-stone-700 wdl:px-2 wdl:pb-1.5 wdl:pt-1.5 wdl:text-md wdl:hover:bg-[#F3F3F3] wdl:hover:text-primary wdl:hover:underline wml:px-1 wml:pb-2.5 wml:pl-[14px] wml:pr-[14px] wml:pt-2.5 wml:text-lg wml:font-medium wml:active:bg-primary-lightened wml:active:text-primary':
          !props.searchWizard,
        'wd:bg-[#F3F3F3] wd:text-primary wd:underline':
          props.highlighted && props.searchWizard && !props.highlightWithIndex,
        'wdl:bg-[#F3F3F3] wdl:text-primary wdl:underline':
          props.highlighted && !props.searchWizard && !props.highlightWithIndex,
      })}
      onClick={addItem}
    >
      {props.text}
    </div>
  )
}

interface SearchResultPerCategoryProps extends SearchResult {
  totalStartIndex: number
  highlightedSearchResultIndex: number | null
  selectedItems: SearchItem[]
  setSelectedItems: (items: SearchItem[]) => AnyAction
  searchWizard: boolean | undefined
  highlightWithIndex: boolean
}

function filterSearchResults(results: SearchItem[], selectedItems: SearchItem[]): SearchItem[] {
  if (!results) {
    return []
  }
  return results.filter(
    (r) =>
      !selectedItems.find(
        (selectedItem) => r.value === selectedItem.value && r.type === selectedItem.type,
      ),
  )
}

function SearchResultPerCategory(props: SearchResultPerCategoryProps): ReactElement | null {
  const searchItems = filterSearchResults(props.results, props.selectedItems)
  const t = useTranslations('Wizard')

  if (searchItems.length === 0) {
    return null
  }

  return (
    <>
      <div
        className={classNames({
          'bg-[#EDECEC] font-medium text-stone-600 wd:mb-1 wd:px-2 wd:text-sm wm:ml-[14px] wm:mr-[14px] wm:pl-1 wm:text-sm':
            props.searchWizard,
          'bg-[#EDECEC] font-medium text-stone-600 wdl:mb-1 wdl:px-2 wdl:text-sm wml:ml-[14px] wml:mr-[14px] wml:pl-1 wml:text-sm':
            !props.searchWizard,
        })}
      >
        {t(props.type)}
      </div>
      {searchItems.map((r, index) => (
        <SingleSearchResult
          highlighted={props.totalStartIndex + index === props.highlightedSearchResultIndex}
          key={r.value + r.type}
          {...r}
          selectedItems={props.selectedItems}
          setSelectedItems={props.setSelectedItems}
          searchWizard={props.searchWizard}
          highlightWithIndex={props.highlightWithIndex}
        />
      ))}
    </>
  )
}

interface SelectedItemProps extends SearchItem {
  selectedItems: SearchItem[]
  setSelectedItems: (items: SearchItem[]) => AnyAction
}

function SelectedItem(props: SelectedItemProps): ReactElement {
  const dispatch = useDispatch()

  function deselectItem(code: string): void {
    dispatch(props.setSelectedItems(props.selectedItems.filter((i) => i.value !== code)))
  }

  return (
    <div
      className="relative mb-2 mr-2 inline-block cursor-pointer truncate rounded-md bg-primary-lightened px-2 py-1 pl-[24px] pr-3 font-medium text-primary wd:text-sm wm:mb-2 wm:max-w-full wm:text-md"
      onClick={() => deselectItem(props.value)}
    >
      <div className="absolute -bottom-[50%] -left-[10px] -top-[19%] flex h-[44px] w-[44px] flex-row items-center justify-center">
        <svg
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          className="fill-primary"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1.90381 11.0962C0.68482 9.87721 0 8.22391 0 6.5C0 4.77609 0.68482 3.12279 1.90381 1.90381C3.12279 0.68482 4.77609 0 6.5 0C8.22391 0 9.87721 0.68482 11.0962 1.90381C12.3152 3.12279 13 4.77609 13 6.5C13 8.22391 12.3152 9.87721 11.0962 11.0962C9.87721 12.3152 8.22391 13 6.5 13C4.77609 13 3.12279 12.3152 1.90381 11.0962ZM8.84048 4.15601C9.05213 4.36505 9.05332 4.70515 8.84314 4.91565L7.26116 6.5L8.84314 8.08435C9.05332 8.29485 9.05213 8.63496 8.84048 8.84399C8.62882 9.05303 8.28686 9.05185 8.07667 8.84135L6.5 7.26231L4.92333 8.84135C4.71315 9.05185 4.37118 9.05303 4.15952 8.84399C3.94787 8.63496 3.94668 8.29485 4.15686 8.08435L5.73884 6.5L4.15686 4.91565C3.94668 4.70515 3.94787 4.36505 4.15952 4.15601C4.37118 3.94697 4.71315 3.94815 4.92333 4.15865L6.5 5.73769L8.07667 4.15865C8.28686 3.94815 8.62882 3.94697 8.84048 4.15601Z"
            fill=""
          />
        </svg>
      </div>
      {props.text || props.label}
    </div>
  )
}

function allSearchItems(selectedItems: SearchItem[], searchResults: SearchResult[]): SearchItem[] {
  return searchResults?.flatMap((r) => filterSearchResults(r.results, selectedItems)) ?? []
}

function numberOfAllSearchResults(
  selectedItems: SearchItem[],
  searchResults: SearchResult[],
): number {
  return allSearchItems(selectedItems, searchResults).length ?? 0
}

export default function SearchBox(props: SearchBoxProps): ReactElement | null {
  const [highlightedSearchResultIndex, setHighlightedSearchResultIndex] = useState<number | null>(0)
  const dispatch = useDispatch()
  const [hovered, setHovered] = useState(false)
  const t = useTranslations('Wizard')

  useEffect(() => {
    function listenToKeyEvents(
      event: CustomEvent<{ key: 'Enter' | 'ArrowUp' | 'ArrowDown' }>,
    ): void {
      const allResults = allSearchItems(props.selectedItems, props.searchResults || [])
      const numberOfTotalSearchResults = numberOfAllSearchResults(
        props.selectedItems,
        props.searchResults || [],
      )

      if (
        event.detail.key === 'ArrowDown' &&
        (highlightedSearchResultIndex === null ||
          highlightedSearchResultIndex < numberOfTotalSearchResults - 1)
      ) {
        setHighlightedSearchResultIndex(
          highlightedSearchResultIndex === null ? 0 : highlightedSearchResultIndex + 1,
        )
      }
      if (
        event.detail.key === 'ArrowUp' &&
        highlightedSearchResultIndex !== null &&
        highlightedSearchResultIndex >= 1
      ) {
        setHighlightedSearchResultIndex(
          highlightedSearchResultIndex === null ? 0 : highlightedSearchResultIndex - 1,
        )
      }

      if (event.detail.key === 'Enter' && highlightedSearchResultIndex !== null) {
        const result = allResults[highlightedSearchResultIndex]

        if (result) {
          dispatch(props.setSelectedItems([...props.selectedItems, { ...result }]))
        }
      }
    }

    window.addEventListener(WIZARD_INPUT_KEY_PRESSED, listenToKeyEvents)

    return () => window.removeEventListener(WIZARD_INPUT_KEY_PRESSED, listenToKeyEvents)
  }, [highlightedSearchResultIndex, props.searchResults, props.selectedItems])

  useEffect(() => {
    const numberSearchResults = numberOfAllSearchResults(
      props.selectedItems,
      props.searchResults || [],
    )
    if (
      highlightedSearchResultIndex !== null &&
      highlightedSearchResultIndex >= numberSearchResults
    ) {
      setHighlightedSearchResultIndex(numberSearchResults - 1)
    }
  })

  useEffect(() => {
    function resetIndex(): void {
      setHighlightedSearchResultIndex(0)
    }

    window.addEventListener('WIZARD_INPUT_BLUR', resetIndex)

    return () => window.removeEventListener('WIZARD_INPUT_BLUR', resetIndex)
  }, [])

  if (props.searchResults) {
    return (
      <DropDown
        setHovered={setHovered}
        {...props}
        additionalClassName={classNames({
          'w-full right-0 wd:max-w-[450px] wd:w-[450px] wd:max-h-[calc(100vh-150px)] overflow-y-auto left-0':
            props.searchWizard,
          'w-full right-0 left-0 wdl:max-w-[450px] wdl:w-[450px] overflow-y-auto':
            !props.searchWizard,
        })}
      >
        <div
          className={classNames({
            'visible flex flex-wrap wd:bg-white wd:px-[7px] wm:pb-2 wm:pl-[14px] wm:pr-[14px] wm:pt-3.5':
              props.searchWizard,
            'visible flex flex-wrap wdl:bg-white wdl:px-[7px] wml:pb-2 wml:pl-[14px] wml:pr-[14px] wml:pt-3.5':
              !props.searchWizard,
          })}
        >
          {props.selectedItems.map((item) => (
            <SelectedItem
              key={item.value}
              {...item}
              selectedItems={props.selectedItems}
              setSelectedItems={props.setSelectedItems}
            />
          ))}
        </div>
        <div
          className={classNames({
            'no-scrollbar wizard-search-result-list search wd:h-auto wd:bg-white wm:h-[calc(100vh_-_250px)] wm:overflow-y-scroll wm:pb-[100px]':
              props.searchWizard,
            'no-scrollbar wizard-search-result-list landing-page wdl:h-auto wdl:bg-white wml:h-[calc(100vh_-_250px)] wml:overflow-y-scroll wml:pb-[100px]':
              !props.searchWizard,
          })}
        >
          {props.searchResults.map((r, index) => (
            <SearchResultPerCategory
              totalStartIndex={
                index > 0
                  ? props.searchResults
                      ?.slice(0, index)
                      .flatMap((r) => filterSearchResults(r.results, props.selectedItems)).length ||
                    0
                  : 0
              }
              highlightedSearchResultIndex={highlightedSearchResultIndex}
              key={r.type}
              {...r}
              selectedItems={props.selectedItems}
              setSelectedItems={props.setSelectedItems}
              searchWizard={props.searchWizard}
              highlightWithIndex={hovered}
            />
          ))}
        </div>
        {props.searchResults.flatMap((r) => r.results).length === 0 &&
          props.resultSearchTerm !== '' && (
            <div
              className={classNames({
                'flex flex-wrap pb-6 text-stone-400 wd:px-4 wd:pt-3': props.searchWizard,
                'flex flex-wrap pb-6 text-stone-400 wdl:px-4 wdl:pt-3': !props.searchWizard,
              })}
            >
              {t('noResults')}:{' '}
              <div className="ml-1 inline-block w-auto max-w-[400px] truncate font-medium text-stone-500">
                &quot;{props.resultSearchTerm}&quot;
              </div>{' '}
            </div>
          )}
      </DropDown>
    )
  }

  return null
}
